/* eslint-disable no-template-curly-in-string */
import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Col, Row } from 'antd';
import { useSelector } from 'react-redux';

import { LoadingContainer } from 'app/components/LoadingContainer';
import useOrder, {
  I_TicketItemByPark,
  OnChangeTicketAmount,
  OnUpdateOrder,
  OnUpdateTicket,
  OnUpdateTicketList,
} from './hooks/useOrder';
import useWindowSize from 'app/hooks/useWindowSize';
import TicketList from './components/TicketList';
import PaymentInfo from './components/PaymentInfo';
import { PaymentMethod } from '../Cart/hooks/useCartNew';
import { I_TicketData, I_TicketDataProcessed } from './types';
import { generateUUID } from 'utils/helper';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { useCheckoutSlice } from 'app/pages/Checkout/slice';
import NotFound from '../NotFound';
import { CountdownTimer } from 'utils/time';
import ContainerFluid from 'app/components/Layout/ContainerFluid';
import RenderIf from 'app/components/RenderIf';
import AddTicket from './components/AddTicket';
import useFetchData from 'app/hooks/useFetchData';
import { getListParkApi } from 'app/services/homeApi';
import {
  getCatalogApi,
  getPriceCartApi,
  getTicketAddons,
} from 'app/services/showApi';
import { APPROVAL_ORDER_STATUS } from '../NewAccount/ApproveOrder';
import { getDetailOrderApi } from 'app/services/checkoutApi';
import { AddonsTicketModal } from 'app/components/AddonsTicketModal';
import { useGlobalFunction } from '../App/hooks/useGlobalFunction';
import _ from 'lodash';
import moment from 'moment';
import { TimeoutModal } from '../Checkout/ConfirmPolicyOrder';
import { useCheckCartConfig } from '../Checkout/hooks/useCheckCartConfig';

export type I_OrderContext = {
  orderId: string;
  orderIdHash: string;
  ticketsSortedByPark: I_TicketItemByPark[];
  onChangeTicketAmount: OnChangeTicketAmount;
  totalNumberTickets: number;
  totalPrice: number;
  onUpdateOrder: OnUpdateOrder;
  ticketList: I_TicketDataProcessed[];
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  paymentMethod: PaymentMethod;
  setPaymentMethod: (value: PaymentMethod) => void;
  deliverTicketType: number | null;
  setDeliverTicketType: (value: number | null) => void;
  onChangePaymentMethod: (value?: any) => void;
  onUpdateTicket: OnUpdateTicket;
  onUpdateTicketList: OnUpdateTicketList;
  countDownId: string;
  setCountDownId: (id: string) => void;
  orderDetailData?: Record<string, any>;
  pendingCountdownOrder: (
    orderHashId: string,
    pending: '0' | '1',
  ) => Promise<void>;
  onChangeAmountError: (identify: string, newAmount: number) => void;
  onRemoveTicket: (identify: string) => void;
  onSetTicket: (
    setter: (prev: I_TicketDataProcessed[]) => I_TicketDataProcessed[],
  ) => void;
  ticketsMergedNeedUpdateAmount: MutableRefObject<string[]>;
  setEnablePaymentAction: Dispatch<SetStateAction<boolean>>;
  isSubmitted: boolean;
  setIsSubmitted: Dispatch<SetStateAction<boolean>>;
  originOrderDetailData: Record<string, any>;
  ticketsGettingPrice: string[];
  setTicketsGettingPrice: Dispatch<SetStateAction<string[]>>;
  bpAllocatedDebt?: number | null;
  setOrderDetailData: Dispatch<SetStateAction<Record<string, any> | undefined>>;
};

export enum EDIT_ORDER_STEPS {
  EDIT_TICKET_PROPERTY,
  ADD_TICKET,
}

export const OrderContext = createContext<I_OrderContext>({} as I_OrderContext);

const EditOrder = () => {
  const params: { hashId: string } = useParams();
  const orderHashId = params?.hashId;
  const { device } = useWindowSize();
  const {
    orderId,
    orderIdHash,
    ticketsSortedByPark,
    onChangeTicketAmount,
    paymentInfo,
    totalNumberTickets,
    totalPrice,
    onGetTicketsPrice,
    onUpdateOrder,
    isLoadingData,
    ticketList,
    onUpdateTicket,
    onUpdateTicketList,
    countDownId,
    setCountDownId,
    pendingCountdownOrder,
    orderDetailData,
    isGetOrderDataFailed,
    onAddingTicket,
    onRemoveTicket,
    onSetTicket,
    originOrderDetailData,
    setOrderDetailData,
  } = useOrder(orderHashId);
  useCheckoutSlice();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [isShowTimeout, setIsShowTimeout] = useState(false);
  const history = useHistory();
  const [deliverTicketType, setDeliverTicketType] = useState<number | null>(
    null,
  );
  const isAbleGetCartPrice = useRef<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingNewTicket, setIsAddingNewTicket] = useState(false);
  const countdownTimerRef = useRef<CountdownTimer>();
  const isPreparingAddingTicketData = useRef(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ticketsGettingPrice, setTicketsGettingPrice] = useState<string[]>([]);

  const ticketsMergedNeedUpdateAmount = useRef([]);

  const [currentStep, setCurrentStep] = useState<EDIT_ORDER_STEPS>(
    EDIT_ORDER_STEPS.EDIT_TICKET_PROPERTY,
  );

  const [ticketAdded, setTicketAdded] = useState<
    Record<string, Partial<I_TicketData>>
  >({});

  const [enablePaymentAction, setEnablePaymentAction] = useState(false);

  const [isShowAddonsModal, setIsShowAddonsModal] = useState<boolean>(false);
  const [shouldShowAddonsModal, setShouldShowAddonsModal] =
    useState<boolean>(true);
  const [listItems, setListItems] = useState<any>([]);
  const [listAddons, setListAddons] = useState<any>([]);
  const debounceTimeout = useRef<Record<string, NodeJS.Timeout>>({});
  const [isAddonsLoading, setIsAddonsLoading] = useState<boolean>(false);
  const translations = useSelector(selectTranslations);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const [readyToProcess, setReadyToProcess] = useState<
    Record<string, 'IDLE' | 'PENDING' | 'READY'>
  >({
    update: 'IDLE',
    pay: 'IDLE',
  });
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [dateAddons, setDateAddons] = useState<string>(
    moment().format('YYYY-MM-DD'),
  );
  const [disableCreateOrder, setDisableCreateOrder] = useState(false);
  let hasChangeProperty = useRef(false);
  let isChangingNumberRef = useRef(false);
  useEffect(() => {
    setEnablePaymentAction(
      orderDetailData?.subOrderState?.value !== APPROVAL_ORDER_STATUS.REFUSE ||
        !!orderDetailData?.hadUpdated,
    );
  }, [orderDetailData?.hadUpdated, orderDetailData?.subOrderState?.value]);

  const onChangePaymentMethod = () => {
    isAbleGetCartPrice.current = true;
  };
  const onChangeAmount = useCallback(
    (identify: string, newAmount: number) => {
      isAbleGetCartPrice.current = true;
      onChangeTicketAmount(identify, newAmount);
    },
    [onChangeTicketAmount],
  );

  const onChangeAmountError = (identify: string, newAmount: number) => {
    onChangeTicketAmount(identify, newAmount);
  };

  const handleRemoveTicket = useCallback(
    (identify: string) => {
      isAbleGetCartPrice.current = true;
      onRemoveTicket(identify);
    },
    [onRemoveTicket],
  );

  const [preparingAddTicketData, setPreparingAddTicketData] = useState<{
    parks: Record<string, any>[];
    catalogs: Record<string, any>[];
  }>({
    parks: [],
    catalogs: [],
  });

  const { onFetchData: onFetchParkData, isLoading: isLoadingParkData } =
    useFetchData({
      actionApi: getListParkApi,
      IIFE: false,
      takeFirstRequest: true,
    });

  const { onFetchData: onFetchCatalogData, isLoading: isLoadingCatalogData } =
    useFetchData({
      actionApi: getCatalogApi,
      IIFE: false,
      payloadData: '',
      takeFirstRequest: true,
    });
  const { checkConfig } = useCheckCartConfig();
  const onPrepareAddingTicketData = async () => {
    if (isPreparingAddingTicketData.current) return;
    isPreparingAddingTicketData.current = true;

    setCountDownId(generateUUID());

    const [parkResult, catalogResult] = await Promise.all([
      onFetchParkData(),
      onFetchCatalogData(),
    ]);

    setCurrentStep(EDIT_ORDER_STEPS.ADD_TICKET);
    setPreparingAddTicketData({
      parks: parkResult?.data?.map(item => ({
        ...item,
        label: item?.name,
        value: item?.id,
      })),
      catalogs: catalogResult?.data?.map(item => ({
        ...item,
        key: item?.id,
      })),
    });

    isPreparingAddingTicketData.current = false;

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleCancelAddingTicket = () => {
    if (isAddingNewTicket) return;
    setCountDownId(generateUUID());

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    setCurrentStep(EDIT_ORDER_STEPS.EDIT_TICKET_PROPERTY);
    setTicketAdded({});
  };

  const handleAcceptAddingTicket = async () => {
    if (isAddingNewTicket) return;
    setIsAddingNewTicket(true);
    setCountDownId(generateUUID());
    await onAddingTicket(
      ticketAdded,
      paymentMethod,
      orderDetailData?.businessPartnerAllocatedDebt?.id,
    );
    setIsAddingNewTicket(false);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    setCurrentStep(EDIT_ORDER_STEPS.EDIT_TICKET_PROPERTY);
    setTicketAdded({});
    setEnablePaymentAction(true);
  };

  const onTimeoutEditOrder = async () => {
    if (isLoading) return;
    setIsShowTimeout(false);
    try {
      setIsLoading(true);
      const response = await getDetailOrderApi(orderHashId, true);
      const orderState = response.data?.orderState?.value;
      if (orderState === 'paymentPending') return history.push('/account');
      history.push('/payment-callback?vnp_TxnRef=' + orderDetailData?.idHash);
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAbleGetCartPrice.current) {
      onGetTicketsPrice({
        paymentMethod,
        bpAllocatedDebt: orderDetailData?.businessPartnerAllocatedDebt?.id,
        onStartApi: () => setIsLoading(true),
        onSuccess: listCartToGetPrice => {
          isChangingNumberRef.current = false;
          setIsLoading(false);
          setTicketsGettingPrice(prev =>
            prev.filter(i => {
              const isGettedPrice = listCartToGetPrice.find(
                j => j?.identify === i,
              );
              return !isGettedPrice;
            }),
          );
        },
        onError: listCartToGetPrice => {
          isChangingNumberRef.current = false;
          setIsLoading(false);
          setTicketsGettingPrice(prev =>
            prev.filter(i => {
              const isGettedPrice = listCartToGetPrice.find(
                j => j?.identify === i,
              );
              return !isGettedPrice;
            }),
          );
        },
      });
      isAbleGetCartPrice.current = false;
    }
  }, [
    onGetTicketsPrice,
    paymentMethod,
    totalNumberTickets,
    orderDetailData?.businessPartnerAllocatedDebt?.id,
  ]);

  useEffect(() => {
    if (paymentInfo?.id) {
      setPaymentMethod(paymentInfo?.id);
    }
  }, [paymentInfo?.id]);

  useEffect(() => {
    const countDownSecond = 10 * 60;
    const listener = async () => {
      await pendingCountdownOrder(orderHashId, '0');
      setIsShowTimeout(true);
    };
    if (!countdownTimerRef.current) {
      countdownTimerRef.current = new CountdownTimer(countDownSecond);
    }
    countdownTimerRef.current.addListener(listener);

    return () => {
      countdownTimerRef.current?.removeListener(listener);
    };
  }, [orderHashId, pendingCountdownOrder]);

  useEffect(() => {
    const countDownSecond = 10 * 60;
    countdownTimerRef.current?.resetTo(countDownSecond);
    countdownTimerRef.current?.start(1000);
  }, [countDownId]);

  useEffect(() => {
    return () => {
      countdownTimerRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    return () => {
      sessionStorage.setItem(
        'ticketType',
        sessionStorage.getItem('prevTicketType') || '',
      );
    };
  }, []);

  const getPrice = useCallback(
    async (
      params: {
        date: string;
        show_id: string;
        amount: string;
      },
      ticketData?: any,
    ) => {
      try {
        setIsAddonsLoading(true);
        const formData = new FormData();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(`items[0][${key}]`, value);
          }
        });
        formData.append('items[0][payment]', paymentMethod as any);
        const bpAllocatedDebt =
          orderDetailData?.businessPartnerAllocatedDebt?.id;
        if (bpAllocatedDebt !== null && bpAllocatedDebt !== undefined)
          formData.append('items[0][bpAllocatedDebt]', bpAllocatedDebt);
        const res = await getPriceCartApi(formData);
        if (res?.data?.[0]?.error) {
          setListAddons(prev => {
            return [...prev].map(item => {
              if (item?.id === ticketData?.id) {
                return {
                  ...item,
                  error: res?.data?.[0]?.error?.message,
                };
              }
              return item;
            });
          });
          return;
        }

        if (res?.data?.[0]?.errorTicket) {
          setListAddons(prev => {
            return [...prev].map(item => {
              if (item?.id === ticketData?.id) {
                return {
                  ...item,
                  error: res?.data?.[0]?.errorTicket,
                };
              }
              return item;
            });
          });
          return;
        }

        if (
          res?.status === 200 &&
          _.isArray(res?.data) &&
          res?.data?.length > 0
        ) {
          const dataInventory = res?.data[0];

          setListAddons(prev => {
            return [...prev].map(item => {
              if (item?.id === ticketData?.id) {
                return {
                  ...item,
                  price: +dataInventory?.price,
                  inventory: +dataInventory?.inventory,
                  date: dateAddons,
                  displayPrice:
                    +item?.amount > 0
                      ? +dataInventory?.price * item?.amount
                      : '0',
                };
              }
              return item;
            });
          });
          return;
        }
      } catch (err: any) {
        setListAddons(prev => {
          return [...prev].map(item => {
            if (item?.id === ticketData?.id) {
              return {
                ...item,
                error: err?.message,
              };
            }
            return item;
          });
        });
        return;
      } finally {
        setIsAddonsLoading(false);
        setDisableSubmit(false);
      }
    },
    [
      dateAddons,
      paymentMethod,
      orderDetailData?.businessPartnerAllocatedDebt?.id,
    ],
  );
  const buildTicketErrorMap = (
    tickets?: { ticket_id: string | number; message: unknown; date: string }[],
  ) => {
    const map = new Map<string, string>();
    if (!tickets) return map;
    for (const t of tickets) {
      if (typeof t.message === 'string') {
        const keyMap = `${t.ticket_id}-${t.date}`;
        map.set(String(keyMap), t.message);
      }
    }
    return map;
  };
  const fetchAddons = useCallback(async () => {
    try {
      setIsAddonsLoading(true);
      const {
        data: { data: res },
      } = await getTicketAddons({
        showIds: ticketList.map(item => item?.show?.id).join(',') as any,
      });

      const validAddons = res
        ? res.filter(
            (item: any) =>
              !ticketList.find(
                (submitItem: any) => submitItem?.show?.id === item?.id,
              ),
          )
        : [];

      if (validAddons?.length === 0) {
        setShouldShowAddonsModal(false);
        return;
      } else {
        setIsLoading(false);
        setIsShowAddonsModal(true);
        setShouldShowAddonsModal(true);
      }

      setListAddons(
        validAddons.map(item => {
          return {
            ...item,
            amount: '0',
            date: dateAddons,
            isAddon: true,
            displayPrice: '0',
          };
        }),
      );

      await Promise.all(
        [...validAddons].map((item: any) => {
          return getPrice(
            {
              date: dateAddons,
              show_id: item?.id,
              amount: item?.amount || 1,
            },
            {
              ...item,
            },
          );
        }),
      );
    } catch (err: any) {
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        title: '',
        content:
          err?.data?.dataRequest?.[0]?.message ||
          err?.message.replaceAll('\\n', ''),
        closable: false,
        buttons: [
          {
            text: translations ? translations['b2c.txt.confirm'] : '',
            bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
            color: 'white',
            onClick: () => {
              onCloseDialogNoti();
            },
          },
        ],
      });
    } finally {
      setIsAddonsLoading(false);
    }
  }, [
    dateAddons,
    getPrice,
    onCloseDialogNoti,
    onOpenDialogNoti,
    ticketList,
    translations,
  ]);

  const resetTimeout = useCallback(
    (addonTicket, event?: any) => {
      if (debounceTimeout.current?.[addonTicket?.id as string]) {
        clearTimeout(debounceTimeout.current?.[addonTicket?.id as string]);
      }
      debounceTimeout.current[addonTicket?.id as string] = setTimeout(() => {
        getPrice(
          {
            date: dateAddons,
            show_id: addonTicket?.id,
            amount: addonTicket?.amount,
          },
          {
            ...addonTicket,
            jumpValue: addonTicket?.jumpValueB2b,
            minAmountB2b: addonTicket?.minAmountB2b,
            event,
          },
        );
      }, 500);
    },
    [dateAddons, getPrice],
  );

  const handleChangeAddonsAmount = useCallback(
    (value, id, validByJumpValue, event) => {
      setDisableSubmit(true);
      let updatedAddons = [...listAddons];
      updatedAddons = updatedAddons.map(item =>
        item?.id === id
          ? {
              ...item,
              error: null,
            }
          : item,
      );
      setListAddons(updatedAddons);

      const addonTicket = listAddons.find(item => item.id === id);

      if (!validByJumpValue && value >= addonTicket?.minAmountB2b) {
        updatedAddons = updatedAddons.map(item =>
          item.id === id
            ? {
                ...item,
                error: translations?.['b2b.txt.onlyAmount']?.replace(
                  '${jumpValue}',
                  item?.jumpValueB2b,
                ),
              }
            : item,
        );
        setListAddons(updatedAddons);
        setDisableSubmit(false);
        return;
      }

      if (
        value === undefined ||
        value === null ||
        (!validByJumpValue && value >= addonTicket?.jumpValueB2b)
      ) {
        setDisableSubmit(false);
        return;
      }

      if (
        addonTicket?.minAmountB2b &&
        value < addonTicket?.minAmountB2b &&
        value > 0
      ) {
        updatedAddons = updatedAddons.map(item =>
          item.id === id
            ? {
                ...item,
                error: translations?.['b2b.txt.minAmount']?.replace(
                  '${amount}',
                  addonTicket?.minAmountB2b,
                ),
              }
            : item,
        );
        setListAddons(updatedAddons);
        setDisableSubmit(false);
        return;
      }

      updatedAddons = updatedAddons.map(item =>
        item.id === id
          ? {
              ...item,
              amount: value,
            }
          : item,
      );

      event === 'blur' &&
        (validByJumpValue || value === 0) &&
        resetTimeout(
          updatedAddons.find(item => item.id === id),
          event,
        );

      setListAddons(updatedAddons);
    },
    [listAddons, resetTimeout, translations],
  );

  const handleChangeDate = useCallback(
    async value => {
      setListAddons(prev => {
        return [...prev].map(item => {
          return {
            ...item,
            error: null,
            amount: 0,
          };
        });
      });
      setDateAddons(moment(value).format('YYYY-MM-DD'));
      await Promise.all(
        [...listAddons].map((item: any) => {
          return getPrice(
            {
              date: moment(value).format('YYYY-MM-DD'),
              show_id: item?.id,
              amount: item?.amount,
            },
            {
              ...item,
            },
          );
        }),
      );
    },
    [listAddons, getPrice],
  );

  const setReady = useCallback(() => {
    readyToProcess.update === 'PENDING' &&
      setReadyToProcess(prev => {
        return {
          ...prev,
          update: 'READY',
        };
      });
    readyToProcess.pay === 'PENDING' &&
      setReadyToProcess(prev => {
        return {
          ...prev,
          pay: 'READY',
        };
      });
  }, [readyToProcess.pay, readyToProcess.update]);

  const handleAdd = useCallback(() => {
    setListItems(() => {
      return [...ticketList, ...listAddons.filter(item => item?.amount > 0)];
    });
    setIsShowAddonsModal(false);
    setReady();
  }, [listAddons, setReady, ticketList]);
  const validateTickets = useCallback(async () => {
    const paramCheckConfig = ticketList.map(i => ({
      ticket_id: i?.product?.id || '',
      date: i?.date?.dateOrigin
        ? i?.date?.dateOrigin
        : i.date?.date
        ? moment(i.date?.date, 'YYYY/MM/DD').format('YYYY-MM-DD')
        : '',
      quantity: i?.amount?.toString() ?? '',
      ticketType: sessionStorage.getItem('ticketType') || '',
    }));
    const res = await checkConfig(paramCheckConfig, {
      onStart: () => setIsLoading(true),
      onFinish: () => setIsLoading(false),
      onError: (error, source) => {
        if (source === 'exception') return;
        const ticketErrorMap = buildTicketErrorMap(error?.tickets);
        for (const ele of ticketList) {
          const ticketId = ele?.product?.id;
          const date = ele?.date?.dateOrigin
            ? ele?.date?.dateOrigin
            : ele.date?.date
            ? moment(ele.date?.date, 'YYYY/MM/DD').format('YYYY-MM-DD')
            : '';
          const keyMap = `${ticketId}-${date}`;
          if (!ticketId) continue;
          const err = ticketErrorMap.get(String(keyMap));
          if (!err) continue;
          onUpdateTicket(ele.identify, {
            errorTime: err,
          });
        }
      },
    });
    return res;
  }, [ticketList, checkConfig, onUpdateTicket]);
  const handleAddons = useCallback(
    async (action: 'PAY' | 'UPDATE') => {
      setListAddons([]);
      for (const ele of ticketList) {
        onUpdateTicket(ele.identify, {
          errorTime: '',
        });
      }
      if (isChangingNumberRef.current || isLoading) return;
      const resCheck = await validateTickets();
      if (!resCheck?.ok) return;
      setReadyToProcess({
        update: action === 'UPDATE' ? 'PENDING' : 'IDLE',
        pay: action === 'PAY' ? 'PENDING' : 'IDLE',
      });
      await fetchAddons();
    },
    [fetchAddons, validateTickets, isLoading],
  );

  const handleCloseAddonsModal = () => {
    setDateAddons(moment().format('YYYY-MM-DD'));
    setListAddons([]);
    setIsShowAddonsModal(false);
    setReady();
  };

  const onChangeDeliverTicketType = useCallback(
    (value: number | null) => {
      setDeliverTicketType(value);
      setCountDownId(generateUUID());
    },
    [setCountDownId],
  );

  if (isGetOrderDataFailed) return <NotFound />;

  return (
    <>
      <OrderContext.Provider
        value={{
          ticketsSortedByPark,
          orderId,
          orderIdHash,
          onChangeTicketAmount: onChangeAmount,
          isLoading,
          setIsLoading,
          paymentMethod,
          setPaymentMethod: (value: PaymentMethod) => {
            setPaymentMethod(value);
            setCountDownId(generateUUID());
          },
          deliverTicketType,
          setDeliverTicketType: onChangeDeliverTicketType,
          totalNumberTickets,
          totalPrice,
          onChangePaymentMethod,
          onUpdateOrder: async (payment, deliverId, dataUpdate) => {
            const res = await onUpdateOrder(payment, deliverId, dataUpdate);
            countdownTimerRef.current?.stop();
            return res;
          },
          ticketList,
          onUpdateTicket,
          onUpdateTicketList,
          countDownId,
          setCountDownId,
          orderDetailData,
          pendingCountdownOrder,
          onChangeAmountError,
          onRemoveTicket: handleRemoveTicket,
          onSetTicket,
          ticketsMergedNeedUpdateAmount,
          setEnablePaymentAction,
          isSubmitted,
          setIsSubmitted,
          originOrderDetailData,
          ticketsGettingPrice,
          setTicketsGettingPrice,
          bpAllocatedDebt: orderDetailData?.businessPartnerAllocatedDebt?.id,
          setOrderDetailData,
        }}
      >
        <LoadingContainer
          loading={isLoadingData || isLoading || isAddingNewTicket}
        >
          <ContainerFluid>
            <Col span={24}>
              <RenderIf
                condition={
                  currentStep === EDIT_ORDER_STEPS.EDIT_TICKET_PROPERTY
                }
                fallback={
                  <AddTicket
                    preparingData={preparingAddTicketData}
                    paymentMethod={paymentMethod}
                    setCurrentStep={setCurrentStep}
                    setTicketAdded={setTicketAdded}
                    ticketAdded={ticketAdded}
                    onAcceptAddingTicket={handleAcceptAddingTicket}
                    onCancelAddingTicket={handleCancelAddingTicket}
                    setCountDownId={setCountDownId}
                    isAddingNewTicket={isAddingNewTicket}
                    bpAllocatedDebt={
                      orderDetailData?.businessPartnerAllocatedDebt?.id
                    }
                    partner={orderDetailData?.businessPartnerAllocatedDebt}
                  />
                }
              >
                <>
                  {isLoadingData ? null : (
                    <Row gutter={26}>
                      <Col span={device === 'desktop' ? 16 : 24}>
                        <TicketList
                          hasChangeProperty={hasChangeProperty}
                          disableCreateOrder={disableCreateOrder}
                          setDisableCreateOrder={setDisableCreateOrder}
                          onPrepareAddingTicketData={onPrepareAddingTicketData}
                          isPreparingAddingTicketData={
                            isLoadingParkData || isLoadingCatalogData
                          }
                          isChangingNumberRef={isChangingNumberRef}
                        />
                      </Col>
                      <Col span={device === 'desktop' ? 8 : 24}>
                        <PaymentInfo
                          hasChangeProperty={hasChangeProperty}
                          disableCreateOrder={disableCreateOrder}
                          setDisableCreateOrder={setDisableCreateOrder}
                          enablePaymentAction={enablePaymentAction}
                          setEnablePaymentAction={setEnablePaymentAction}
                          onTimeoutEditOrder={onTimeoutEditOrder}
                          handleAddons={handleAddons}
                          readyToProcess={readyToProcess}
                          setReadyToProcess={setReadyToProcess}
                          listItems={listItems}
                          isShowAddonsModal={isShowAddonsModal}
                          shouldShowAddonsModal={shouldShowAddonsModal}
                          orderDetail={orderDetailData ?? {}}
                          bpAllocatedDebt={
                            orderDetailData?.businessPartnerAllocatedDebt?.id
                          }
                        />
                      </Col>
                    </Row>
                  )}
                </>
              </RenderIf>
            </Col>
          </ContainerFluid>
          <AddonsTicketModal
            open={isShowAddonsModal && shouldShowAddonsModal}
            onClose={handleCloseAddonsModal}
            listAddons={listAddons}
            handleChangeAddonsAmount={handleChangeAddonsAmount}
            handleAdd={handleAdd}
            isLoading={isAddonsLoading}
            handleChangeDate={handleChangeDate}
            disableSubmit={disableSubmit}
            dateAddons={dateAddons}
          />
        </LoadingContainer>
      </OrderContext.Provider>
      <TimeoutModal open={isShowTimeout} onClose={onTimeoutEditOrder} />
    </>
  );
};

export default EditOrder;
